import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  ChevronUpDownIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { useIsAuthenticated, useLogout } from '../../hooks/useAuth';
import { useAppVersion } from '../../hooks/useAppVersion';
import { useOrganizacao } from '../../hooks/useOrganizacao';
import { useNavPermitida, itemAtivo, type NavItemDef } from './navConfig';

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
}

function cx(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

/** Iniciais do usuário pro avatar (ex.: "Edson Lima" → "EL"). */
function iniciais(nome?: string | null): string {
  if (!nome) return '?';
  const partes = nome.trim().split(/\s+/);
  const primeira = partes[0]?.[0] ?? '';
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : '';
  return (primeira + ultima).toUpperCase() || '?';
}

function NavRow({ item, hue, collapsed, mobile, onClick }: { item: NavItemDef; hue: string; collapsed: boolean; mobile?: boolean; onClick?: () => void }) {
  const { pathname } = useLocation();
  const ativo = itemAtivo(item, pathname);
  const Icone = item.icon;

  return (
    <Link
      to={item.to}
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      className={cx(
        'group relative flex items-center gap-2.5 rounded-iris-sm px-1.5 text-[length:var(--fs)] transition-colors duration-150',
        // drawer mobile: touch target ≥ 44px (var(--row-h) fica abaixo disso)
        mobile ? 'min-h-[44px]' : 'h-[var(--row-h)]',
        // ativo SÓLIDO (accent + branco): inconfundível mesmo com heatmap de cores nos chips
        ativo ? 'bg-accent font-medium text-white' : 'text-tx-2 hover:bg-surface-2 hover:text-tx',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring',
        collapsed && 'justify-center px-0',
      )}
    >
      {/* Chip do ícone: cor CATEGÓRICA da seção (navConfig.hue); no ativo vira
          vidro branco sobre o accent. Fundo neutro via color-mix nos tokens —
          funciona nas 4 skins (claro/cinza/azul/preto). */}
      <span
        className={cx('flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-[6px]', ativo && 'bg-white/20')}
        style={ativo ? undefined : { background: 'color-mix(in srgb, var(--text) 7%, transparent)' }}
      >
        <Icone className="h-[calc(var(--icon)-3px)] w-[calc(var(--icon)-3px)]" style={{ color: ativo ? '#fff' : hue }} />
      </span>
      {!collapsed && <span className="truncate">{item.label}</span>}
    </Link>
  );
}

/** Persistência local (config de dispositivo, como densidade — não vai pro servidor). */
const LS_GRUPOS = 'utilyhub_sidebar_grupos_v1';

const Sidebar: React.FC<SidebarProps> = ({ collapsed, mobileOpen, setMobileOpen }) => {
  const { user } = useIsAuthenticated();
  const { mutate: logoutUser } = useLogout();
  const { data: appVersion } = useAppVersion();
  const { data: organizacao } = useOrganizacao();
  const grupos = useNavPermitida();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Seções recolhíveis (COMPRAS/FISCAL/…) — com 20+ itens, o usuário esconde o
  // que não usa. Rail colapsado ignora (mostra todos os ícones).
  const [gruposFechados, setGruposFechados] = useState<Set<string>>(() => {
    try { return new Set<string>(JSON.parse(localStorage.getItem(LS_GRUPOS) || '[]')); } catch { return new Set(); }
  });
  const toggleGrupo = (label: string) => setGruposFechados((s) => {
    const n = new Set(s);
    n.has(label) ? n.delete(label) : n.add(label);
    try { localStorage.setItem(LS_GRUPOS, JSON.stringify([...n])); } catch { /* ignore */ }
    return n;
  });

  // Popover do footer (Meu Perfil / Sair)
  const [menuUsuario, setMenuUsuario] = useState(false);
  useEffect(() => {
    if (!menuUsuario) return;
    const fechar = (e: MouseEvent) => {
      // Fecha ao clicar FORA — via `[data-menu-usuario]` (não um único ref)
      // porque há DUAS instâncias do footer (aside desktop + gaveta mobile).
      // Com um ref só, no mobile ele apontava pro footer desktop escondido, e
      // tocar "Meu Perfil" era tratado como clique-fora → o menu fechava antes
      // do toque navegar (o bug do "perfil não abre no celular").
      if (!(e.target as HTMLElement).closest?.('[data-menu-usuario]')) setMenuUsuario(false);
    };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, [menuUsuario]);

  // Logo da empresa (bytes puros do backend, sem prefixo data:) — mesmo pattern
  // do OrganizacaoPage/CatalogoDigitalPage.
  const logoRaw = organizacao?.logoBase64?.trim() || null;
  const logoEmpresa = logoRaw
    ? logoRaw.startsWith('data:') ? logoRaw : `data:image/png;base64,${logoRaw}`
    : null;
  const nomeEmpresa = organizacao?.nomeFantasia?.trim() || organizacao?.razaoSocial?.trim() || null;

  const conteudo = (isMobile = false) => {
    const recolhido = !isMobile && collapsed;
    return (
      <>
        {/* Header do workspace — 56px */}
        <header className="flex h-[var(--topbar-h)] flex-shrink-0 items-center gap-2.5 border-b border-line px-3">
          {/* Quadrado da marca 28×28 accent */}
          <div className={cx('flex min-w-0 flex-1 items-center gap-2.5', recolhido && 'justify-center')}>
            {logoEmpresa ? (
              <img src={logoEmpresa} alt={nomeEmpresa ?? 'Logo'} className="h-7 w-7 flex-shrink-0 rounded-lg object-contain" />
            ) : (
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-accent text-sm font-extrabold text-white">
                U
              </span>
            )}
            {!recolhido && (
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold tracking-[-0.01em] text-tx">Bn UtilyHub</div>
                {nomeEmpresa && <div className="truncate text-[11px] text-tx-3">{nomeEmpresa}</div>}
              </div>
            )}
            {/* sem chevron aqui: sinalizava um workspace switcher que não existe (affordance falsa) */}
          </div>
          {isMobile && (
            <button
              onClick={() => setMobileOpen(false)}
              className="rounded-iris-sm p-2.5 text-tx-2 transition-colors hover:bg-surface-2 hover:text-tx"
              title="Fechar menu"
              aria-label="Fechar menu"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </header>

        {/* Grupos de navegação — itens `ocultarSidebar` (ex.: Meu Perfil) ficam
            fora daqui (acessíveis pelo rodapé + ⌘K); grupo vazio é omitido. */}
        <nav className="flex-1 space-y-1 overflow-y-auto overflow-x-hidden px-2 py-2">
          {grupos
            .map((g) => ({ ...g, itens: g.itens.filter((i) => !i.ocultarSidebar) }))
            .filter((g) => g.itens.length > 0)
            .map((g, idx) => {
              const fechado = g.label != null && gruposFechados.has(g.label);
              const temAtivo = g.itens.some((i) => itemAtivo(i, pathname));
              return (
                <div key={g.label ?? '__root'}>
                  {g.label ? (
                    recolhido ? (
                      <div className="mx-2 my-2 h-px bg-line" />
                    ) : (
                      <button
                        onClick={() => toggleGrupo(g.label!)}
                        title={fechado ? 'Expandir seção' : 'Recolher seção'}
                        className="flex w-full items-center gap-1.5 rounded-iris-sm px-2.5 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[.04em] text-tx-3 transition-colors hover:text-tx-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
                      >
                        <span>{g.label}</span>
                        {/* seção fechada com a tela ATIVA dentro: pista de onde você está */}
                        {fechado && temAtivo && <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent" />}
                        <ChevronDownIcon className={cx('ml-auto h-3 w-3 flex-shrink-0 transition-transform', fechado && '-rotate-90')} />
                      </button>
                    )
                  ) : idx > 0 ? (
                    <div className="mx-2 my-2 h-px bg-line" />
                  ) : null}
                  {(recolhido || !fechado) && (
                    <div className="space-y-0.5">
                      {g.itens.map((item) => (
                        <NavRow
                          key={item.to}
                          item={item}
                          hue={g.hue}
                          collapsed={recolhido}
                          mobile={isMobile}
                          onClick={isMobile ? () => setMobileOpen(false) : undefined}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </nav>

        {/* Footer do usuário */}
        <footer className="relative flex-shrink-0 border-t border-line p-2" data-menu-usuario>
          <button
            onClick={() => setMenuUsuario((v) => !v)}
            className={cx(
              'flex w-full items-center gap-2.5 rounded-iris-sm p-1.5 text-left transition-colors hover:bg-surface-2',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring',
              recolhido && 'justify-center',
            )}
            title={recolhido ? (user?.nome || user?.login || 'Usuário') : undefined}
          >
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent-text">
              {iniciais(user?.nome || user?.login)}
            </span>
            {!recolhido && (
              <>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[length:var(--fs)] font-medium text-tx">{user?.nome || user?.login}</div>
                  {user?.email && <div className="truncate text-[11px] text-tx-3">{user.email}</div>}
                </div>
                {/* up/down = padrão de account switcher (o popover abre pra CIMA) */}
                <ChevronUpDownIcon className="h-4 w-4 flex-shrink-0 text-tx-3" />
              </>
            )}
          </button>

          {menuUsuario && (
            <div className="absolute bottom-full left-2 right-2 z-50 mb-1 overflow-hidden rounded-iris border border-line bg-surface py-1 shadow-iris-xl">
              <button
                onClick={() => {
                  setMenuUsuario(false);
                  if (isMobile) setMobileOpen(false);
                  navigate('/perfil');
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-tx-2 transition-colors hover:bg-surface-2 hover:text-tx"
              >
                <UserCircleIcon className="h-4 w-4" /> Meu Perfil
              </button>
              <button
                onClick={() => {
                  setMenuUsuario(false);
                  if (isMobile) setMobileOpen(false);
                  logoutUser();
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-danger transition-colors hover:bg-surface-2"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" /> Sair
              </button>
              {appVersion?.version && (
                <div className="border-t border-line px-3 py-1.5 text-center font-mono text-[10px] tracking-tight text-tx-3">
                  Bn UtilyHub v{appVersion.version}
                </div>
              )}
            </div>
          )}
        </footer>
      </>
    );
  };

  // IMPORTANTE (handoff): largura SEM transition — em re-render a transição trava
  // a largura no valor antigo. width+min+max fixos; recolhimento instantâneo.
  const larguraDesktop = collapsed
    ? { width: 64, minWidth: 64, maxWidth: 64 }
    : { width: 'var(--sidebar-w)', minWidth: 'var(--sidebar-w)', maxWidth: 'var(--sidebar-w)' };

  return (
    <>
      {/* Sidebar Desktop */}
      <aside
        style={larguraDesktop}
        className="app-chrome hidden flex-col border-r border-line bg-sidebar-bg md:flex"
      >
        {conteudo()}
      </aside>

      {/* Gaveta Mobile */}
      <div className={cx('fixed inset-0 z-40 md:hidden', !mobileOpen && 'pointer-events-none')}>
        {/* scrim tokenizado (adapta ao tema) — mesmo overlay do CommandPalette */}
        <div
          onClick={() => setMobileOpen(false)}
          style={{ background: 'var(--scrim)' }}
          className={cx('absolute inset-0 transition-opacity', mobileOpen ? 'opacity-100' : 'opacity-0')}
        />
        {/* w-64 fixo de propósito: no mobile a densidade não deve estreitar a gaveta */}
        <div
          className={cx(
            'app-chrome relative h-full w-64 border-r border-line bg-sidebar-bg transition-transform',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex h-full flex-col">{conteudo(true)}</div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
